import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  PowerOff,
  Power,
  RefreshCcw,
  ShieldCheck,
  Trash2,
  XCircle,
  Truck,
  CreditCard,
  HelpCircle,
  Webhook,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FormErrorBlock } from '@/components/common/FormErrorBlock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormField } from '@/components/common/FormField';
import { cn } from '@/lib/utils';
import { dispatchService } from '@/services/dispatch';
import { partnerInboundWebhooksService } from '@/services/observability';
import { warehousesService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import type {
  DispatchPartner,
  PartnerProvider,
  PartnerProviderSpec,
  PartnerService,
  TestConnectionResult,
} from '@/types/dispatch';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the wizard edits this partner; otherwise it creates a new one. */
  existingPartner?: DispatchPartner | null;
}

type StepId =
  | 'provider'
  | 'identity'
  | 'credentials'
  | 'webhook'
  | 'services'
  | 'portal'
  | 'review';

/**
 * Sh-rebuild-B — multi-step partner onboarding wizard.
 *
 * Provider-driven: the wizard fetches the chosen provider's
 * adapter spec and renders Step 3 (credentials) + Step 5
 * (service catalog) from that spec. MANUAL provider skips
 * Steps 3, 4, 5 entirely — there's no API to set up.
 *
 * Test gate: Step 3 won't let the operator move forward until
 * the live testConnection call succeeds (or the provider is
 * MANUAL). Webhook secret is generated server-side on create
 * for non-MANUAL providers; operator copies the URL + secret
 * into the partner's dashboard in Step 4.
 */
export function PartnerOnboardingWizard({ open, onOpenChange, existingPartner }: Props) {
  const qc = useQueryClient();
  const isEdit = !!existingPartner;

  // Wizard state ─────────────────────────────────────────────────
  const [step, setStep] = useState<StepId>('provider');
  const [provider, setProvider] = useState<PartnerProvider>(existingPartner?.provider ?? 'MANUAL');
  const [code, setCode] = useState(existingPartner?.code ?? '');
  const [name, setName] = useState(existingPartner?.name ?? '');
  const [contactName, setContactName] = useState(existingPartner?.contactName ?? '');
  const [contactPhone, setContactPhone] = useState(existingPartner?.contactPhone ?? '');
  const [contactEmail, setContactEmail] = useState(existingPartner?.contactEmail ?? '');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [pickupWarehouseId, setPickupWarehouseId] = useState<string>(
    existingPartner?.pickupWarehouseId ?? '',
  );
  const [autoSchedulePickup, setAutoSchedulePickup] = useState(
    existingPartner?.autoSchedulePickup ?? true,
  );
  const [enabledServices, setEnabledServices] = useState<PartnerService[]>(
    existingPartner?.enabledServices ?? [],
  );
  // The partner row id once created — needed for Step 3 (test) +
  // Step 4 (webhook URL/secret) + Step 5 (PATCH).
  const [partnerId, setPartnerId] = useState<string | null>(existingPartner?.id ?? null);
  const [webhookSecret, setWebhookSecret] = useState<string | null>(existingPartner?.webhookSecret ?? null);
  // Phase 2 — Portal exposure state. Defaults preserve existing
  // behaviour: every partner starts hidden from the portal.
  const [exposedToPortal, setExposedToPortal] = useState<boolean>(
    existingPartner?.exposedToPortal ?? false,
  );
  const [portalDisplayName, setPortalDisplayName] = useState<string>(
    existingPartner?.portalDisplayName ?? '',
  );
  const [portalBaseRate, setPortalBaseRate] = useState<string>(
    existingPartner?.portalBaseRate != null ? String(existingPartner.portalBaseRate) : '',
  );
  // USD portal charge (international orders). INR uses portalBaseRate above.
  const [portalRateUsd, setPortalRateUsd] = useState<string>(
    existingPartner?.portalRates?.USD != null
      ? String(existingPartner.portalRates.USD)
      : '',
  );
  const [portalEtaDays, setPortalEtaDays] = useState<string>(
    existingPartner?.portalEtaDays != null ? String(existingPartner.portalEtaDays) : '',
  );

  // Sync state when sheet opens.
  //
  // Both this wizard instance and the "new partner" instance are mounted
  // on DispatchPartnersPage at all times — the parent toggles `open` and
  // passes `existingPartner` for edit. useState initializers run only
  // once on first mount, so without this effect:
  //   • Create-mode: stale state from a previous create lingers.
  //   • Edit-mode: form fields render blank because `existingPartner` was
  //     null at first mount, and state never re-syncs when it lands.
  //
  // Re-syncs on every open OR when the editing partner changes. Edit
  // mode starts on the Identity step (the provider can't be changed
  // after create — that's effectively a new partner) and pre-fills
  // every captured field.
  useEffect(() => {
    if (!open) return;
    if (existingPartner) {
      setStep('identity');
      setProvider(existingPartner.provider);
      setCode(existingPartner.code);
      setName(existingPartner.name);
      setContactName(existingPartner.contactName ?? '');
      setContactPhone(existingPartner.contactPhone ?? '');
      setContactEmail(existingPartner.contactEmail ?? '');
      // Encrypted credentials aren't exposed by the API; operator
      // re-enters to rotate. Leaving empty == "no change" on save.
      setCredentials({});
      setTestResult(null);
      setPickupWarehouseId(existingPartner.pickupWarehouseId ?? '');
      setEnabledServices(existingPartner.enabledServices ?? []);
      setAutoSchedulePickup(existingPartner.autoSchedulePickup ?? true);
      setPartnerId(existingPartner.id);
      setWebhookSecret(existingPartner.webhookSecret ?? null);
      setExposedToPortal(existingPartner.exposedToPortal ?? false);
      setPortalDisplayName(existingPartner.portalDisplayName ?? '');
      setPortalBaseRate(
        existingPartner.portalBaseRate != null
          ? String(existingPartner.portalBaseRate)
          : '',
      );
      setPortalRateUsd(
        existingPartner.portalRates?.USD != null
          ? String(existingPartner.portalRates.USD)
          : '',
      );
      setPortalEtaDays(
        existingPartner.portalEtaDays != null ? String(existingPartner.portalEtaDays) : '',
      );
    } else {
      setStep('provider');
      setProvider('MANUAL');
      setCode('');
      setName('');
      setContactName('');
      setContactPhone('');
      setContactEmail('');
      setCredentials({});
      setTestResult(null);
      setPickupWarehouseId('');
      setEnabledServices([]);
      setPartnerId(null);
      setWebhookSecret(null);
      setExposedToPortal(false);
      setPortalDisplayName('');
      setPortalBaseRate('');
      setPortalRateUsd('');
      setPortalEtaDays('');
    }
  }, [open, existingPartner?.id]);

  // Provider spec — fetched once provider is picked
  const specQuery = useQuery({
    queryKey: ['dispatch', 'partner-spec', provider],
    queryFn: () => dispatchService.getProviderSpec(provider),
    enabled: open && step !== 'provider',
  });
  const spec = specQuery.data;

  // Warehouses — for Step 5 pickup picker
  const warehousesQuery = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open && step === 'services',
  });

  // Mutations
  const createPartner = useMutation({
    mutationFn: () =>
      dispatchService.createPartner({
        code: code.trim().toUpperCase(),
        name: name.trim(),
        type: provider === 'MANUAL' ? 'MANUAL' : 'COURIER',
        provider,
        contactName: contactName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      }),
    onSuccess: (partner) => {
      setPartnerId(partner.id);
      setWebhookSecret(partner.webhookSecret ?? null);
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
    },
  });

  const testConn = useMutation({
    mutationFn: () =>
      dispatchService.testPartner(partnerId!, credentials),
    onSuccess: (res) => {
      setTestResult(res.testResult);
      setWebhookSecret(res.partner.webhookSecret ?? webhookSecret);
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
    },
    onError: (err: unknown) =>
      setTestResult({
        ok: false,
        error: err instanceof Error ? err.message : 'Network error',
      }),
  });

  const rotateWebhook = useMutation({
    mutationFn: () => dispatchService.rotatePartnerWebhook(partnerId!),
    onSuccess: (p) => {
      setWebhookSecret(p.webhookSecret ?? null);
      toast.success('Webhook secret rotated. Update it in the partner dashboard.');
    },
  });

  // Phase 1 — partner lifecycle actions surfaced on the Review step
  // when in edit mode.
  const partnerStatus = existingPartner?.status ?? 'ACTIVE';
  const toggleStatus = useMutation({
    mutationFn: () =>
      dispatchService.updatePartner(partnerId!, {
        status: partnerStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
      qc.invalidateQueries({ queryKey: ['integrations', 'connected'] });
      toast.success(p.status === 'ACTIVE' ? 'Partner re-activated' : 'Partner inactivated');
    },
  });
  const deletePartner = useMutation({
    mutationFn: () => dispatchService.deletePartner(partnerId!),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
      qc.invalidateQueries({ queryKey: ['integrations', 'connected'] });
      toast.success(
        res.deleted
          ? 'Partner deleted'
          : 'Partner archived (history preserved — past dispatches still resolve it)',
      );
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not delete partner'),
  });

  const finalSave = useMutation({
    mutationFn: () => {
      // Phase 2 — only send portal fields when the toggle is on (or
      // when it was on and now flipped off). Sending `portalBaseRate=0`
      // when never configured would shove a $0 row at customers if the
      // toggle accidentally flipped on later.
      const rate = Number(portalBaseRate);
      const usdRate = Number(portalRateUsd);
      const eta = Number(portalEtaDays);
      // Per-currency portal charge map — INR from the base-rate field, USD
      // from the USD field. Only include currencies the operator filled in.
      const portalRates: Record<string, number> = {};
      if (Number.isFinite(rate) && rate >= 0 && portalBaseRate.trim() !== '') {
        portalRates.INR = rate;
      }
      if (Number.isFinite(usdRate) && usdRate >= 0 && portalRateUsd.trim() !== '') {
        portalRates.USD = usdRate;
      }
      return dispatchService.updatePartner(partnerId!, {
        // On edit, the operator may have changed display name / contact
        // info / pickup warehouse / enabled services — persist all.
        // `code` is intentionally NOT updateable (it's a stable
        // per-tenant identifier referenced by routing rules etc.).
        name: name.trim(),
        contactName: contactName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        pickupWarehouseId: pickupWarehouseId || null,
        enabledServices,
        autoSchedulePickup,
        exposedToPortal,
        portalDisplayName: portalDisplayName.trim() || undefined,
        portalBaseRate: Number.isFinite(rate) && rate >= 0 ? rate : undefined,
        portalRates: Object.keys(portalRates).length > 0 ? portalRates : undefined,
        portalEtaDays: Number.isFinite(eta) && eta > 0 ? eta : undefined,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Partner updated' : 'Partner ready');
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
      qc.invalidateQueries({ queryKey: ['integrations', 'connected'] });
      onOpenChange(false);
    },
  });

  // Steps shown depend on provider + create/edit mode. The provider
  // picker is skipped on edit since the provider is immutable once the
  // partner row exists (changing it is effectively creating a new
  // partner — operators can delete + re-create if needed). The portal
  // exposure step (Phase 2) shows for every provider — operators may
  // want to expose a Manual courier ("Local delivery") to customers
  // too.
  const steps: StepId[] = useMemo(() => {
    const base =
      provider === 'MANUAL'
        ? ['identity', 'portal', 'review']
        : ['identity', 'credentials', 'webhook', 'services', 'portal', 'review'];
    return (isEdit ? base : ['provider', ...base]) as StepId[];
  }, [provider, isEdit]);
  const stepIdx = steps.indexOf(step);
  const isLast = stepIdx === steps.length - 1;

  const goNext = async () => {
    if (step === 'identity' && !partnerId) {
      try {
        await createPartner.mutateAsync();
      } catch {
        // Don't toast — the mutation's `error` surfaces in the
        // wizard's FormErrorBlock so the operator sees the BE's
        // per-field validation reasons inline, not a single-line toast
        // that loses the detail. See feedback_validation_field_details.md.
        return;
      }
    }
    setStep(steps[Math.min(stepIdx + 1, steps.length - 1)]);
  };

  // Step-scoped error surfaced under the step indicator. Picks up
  // whichever mutation is currently errored for the active step.
  const stepError =
    step === 'identity'
      ? createPartner.error
      : step === 'credentials'
        ? testConn.error
        : step === 'webhook'
          ? rotateWebhook.error
          : step === 'review'
            ? finalSave.error
            : null;
  const goBack = () => setStep(steps[Math.max(stepIdx - 1, 0)]);

  // Gates
  const canAdvance = (() => {
    if (step === 'provider') return !!provider;
    if (step === 'identity') return code.trim().length > 0 && name.trim().length > 0;
    if (step === 'credentials') {
      // No credential fields → the step is informational only (the
      // MOCK simulator, future keyless providers). There's nothing to
      // type and no Test Connection button is rendered, so gating on
      // a test result would strand the operator. Always advance.
      if (!spec || spec.credentialFields.length === 0) return true;
      // testConnection unsupported → always advance.
      if (spec.capabilities.testConnection === false) return true;
      // Just-typed creds → must pass Test before advancing.
      if (testResult?.ok === true) return true;
      // Edit mode + partner already connected + operator hasn't typed
      // new creds → keep existing connection; no re-test required.
      const hasTypedCreds = Object.values(credentials).some(
        (v) => v && v.length > 0,
      );
      if (
        isEdit &&
        existingPartner?.connectionStatus === 'CONNECTED' &&
        !hasTypedCreds
      ) {
        return true;
      }
      return false;
    }
    if (step === 'webhook') return true;
    if (step === 'services') return enabledServices.length > 0 || provider === 'MANUAL';
    if (step === 'portal') {
      // Allow advancing when not exposed (operator can skip), or when
      // exposed AND a flat rate is set (rate is the one field with
      // real customer-facing consequences).
      if (!exposedToPortal) return true;
      const rate = Number(portalBaseRate);
      return Number.isFinite(rate) && rate >= 0 && portalBaseRate.trim() !== '';
    }
    return true;
  })();

  const onFinish = async () => {
    if (provider !== 'MANUAL') {
      await finalSave.mutateAsync();
    } else {
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
      qc.invalidateQueries({ queryKey: ['integrations', 'connected'] });
      toast.success('Partner saved');
      onOpenChange(false);
    }
  };

  // Sh-rebuild-D — partner UUID is the path token (globally unique, unguessable;
  // partner.code is only unique per tenant). Until the partner row is created
  // (i.e. after the identity step), we show a placeholder so operators don't
  // copy a stale URL.
  const webhookUrl = partnerId
    ? `https://zafroi.com/api/v1/integrations/tracking/${partnerId}/webhook`
    : 'https://zafroi.com/api/v1/integrations/tracking/<id>/webhook';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border/60 px-7 py-5">
          <DialogTitle className="text-[18px] font-semibold">
            {isEdit ? `Edit ${existingPartner?.name}` : 'New delivery partner'}
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Set up a courier or manual partner. The wizard skips credential + webhook steps for manual partners.
          </DialogDescription>
          <StepIndicator
            steps={steps}
            current={stepIdx}
            onJump={isEdit ? (idx) => setStep(steps[idx]) : undefined}
          />
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="px-7 py-5 space-y-4">
            {stepError ? <FormErrorBlock error={stepError} /> : null}
            {step === 'provider' && (
              <ProviderPicker provider={provider} onPick={setProvider} />
            )}
            {step === 'identity' && (
              <IdentityStep
                code={code}
                name={name}
                contactName={contactName}
                contactPhone={contactPhone}
                contactEmail={contactEmail}
                onCode={setCode}
                onName={setName}
                onContactName={setContactName}
                onContactPhone={setContactPhone}
                onContactEmail={setContactEmail}
                isEdit={isEdit}
              />
            )}
            {step === 'credentials' && spec && (
              <CredentialsStep
                spec={spec}
                credentials={credentials}
                onCredsChange={setCredentials}
                testResult={testResult}
                onTest={() => testConn.mutate()}
                isTesting={testConn.isPending}
                existingPartner={existingPartner}
              />
            )}
            {step === 'webhook' && spec && (
              <WebhookStep
                spec={spec}
                webhookUrl={webhookUrl}
                secret={webhookSecret}
                onRotate={() => rotateWebhook.mutate()}
                isRotating={rotateWebhook.isPending}
                partnerId={partnerId}
                partner={existingPartner ?? null}
              />
            )}
            {step === 'services' && spec && (
              <>
                <PickupServicesStep
                  spec={spec}
                  warehouses={warehousesQuery.data ?? []}
                  pickupWarehouseId={pickupWarehouseId}
                  onPickupChange={setPickupWarehouseId}
                  enabledServices={enabledServices}
                  onServicesChange={setEnabledServices}
                />
                {spec.capabilities.generatePickup && (
                  <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-md border border-border/70 bg-muted/20 px-3 py-2.5">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-3.5 w-3.5 accent-primary"
                      checked={autoSchedulePickup}
                      onChange={(e) => setAutoSchedulePickup(e.target.checked)}
                    />
                    <span className="space-y-0.5">
                      <span className="block text-[12.5px] font-medium text-foreground">
                        Auto-schedule pickup
                      </span>
                      <span className="block text-[11px] text-muted-foreground">
                        As soon as the warehouse finishes Pack &amp; verify and
                        marks the dispatch <strong>Ready for pickup</strong>,
                        automatically ask {provider} to send a courier to collect
                        the packed parcel. Turn off to schedule pickups yourself.
                      </span>
                    </span>
                  </label>
                )}
              </>
            )}
            {step === 'portal' && (
              <PortalStep
                providerLabel={spec?.label ?? provider}
                fallbackName={name}
                exposedToPortal={exposedToPortal}
                onExposedChange={setExposedToPortal}
                portalDisplayName={portalDisplayName}
                onDisplayNameChange={setPortalDisplayName}
                portalBaseRate={portalBaseRate}
                onBaseRateChange={setPortalBaseRate}
                portalRateUsd={portalRateUsd}
                onUsdRateChange={setPortalRateUsd}
                portalEtaDays={portalEtaDays}
                onEtaDaysChange={setPortalEtaDays}
              />
            )}
            {step === 'review' && (
              <>
                <ReviewStep
                  provider={provider}
                  spec={spec}
                  code={code}
                  name={name}
                  testResult={testResult}
                  hasWebhookSecret={!!webhookSecret}
                  pickupWarehouseId={pickupWarehouseId}
                  enabledServices={enabledServices}
                  existingPartner={existingPartner}
                />
                {isEdit && (
                  <PartnerDangerZone
                    status={partnerStatus}
                    isToggling={toggleStatus.isPending}
                    onToggleStatus={() => toggleStatus.mutate()}
                    isDeleting={deletePartner.isPending}
                    onDelete={() => deletePartner.mutate()}
                  />
                )}
              </>
            )}
          </div>
        </ScrollArea>

        <div className="flex items-center justify-between gap-3 border-t border-border/60 bg-muted/30 px-4 py-3">
          <Button
            variant="outline"
            onClick={stepIdx === 0 ? () => onOpenChange(false) : goBack}
            disabled={createPartner.isPending || finalSave.isPending}
          >
            {stepIdx === 0 ? 'Cancel' : <><ArrowLeft className="h-3.5 w-3.5" /> Back</>}
          </Button>
          <div className="hidden text-[10.5px] text-muted-foreground sm:block">
            Step {stepIdx + 1} of {steps.length}
          </div>
          <div className="flex items-center gap-2">
            {/* Save & close — visible from any step in edit mode so the
                operator can edit one section and persist without
                walking the whole wizard. Skipped on create (linear
                wizard is intentional on first-time setup). */}
            {isEdit && !isLast && provider !== 'MANUAL' && (
              <Button
                variant="outline"
                onClick={() => finalSave.mutate(undefined, { onSuccess: () => { toast.success('Partner updated'); onOpenChange(false); } })}
                disabled={finalSave.isPending || createPartner.isPending}
                title="Save current changes without walking through every step"
              >
                {finalSave.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                Save & close
              </Button>
            )}
            {isLast ? (
              <Button onClick={onFinish} disabled={!canAdvance || finalSave.isPending}>
                {finalSave.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                Save partner
              </Button>
            ) : (
              <Button onClick={goNext} disabled={!canAdvance || createPartner.isPending}>
                {createPartner.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Next <ArrowRight className="h-3.5 w-3.5" /></>}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Step indicator ───────────────────────────────────────────────

const STEP_LABEL: Record<StepId, string> = {
  provider: 'Provider',
  identity: 'Identity',
  credentials: 'Credentials',
  webhook: 'Webhook',
  services: 'Pickup + Services',
  portal: 'Portal',
  review: 'Review',
};

function StepIndicator({
  steps,
  current,
  onJump,
}: {
  steps: StepId[];
  current: number;
  /**
   * When set, every non-current step is clickable to jump to. Wired by
   * the wizard only in edit mode — all partner data is pre-loaded then,
   * so forward + backward jumps are safe.
   */
  onJump?: (idx: number) => void;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1">
      {steps.map((s, i) => {
        const isPast = i < current;
        const isCurrent = i === current;
        const clickable = !!onJump && !isCurrent;
        return (
          <button
            key={s}
            type="button"
            onClick={clickable ? () => onJump!(i) : undefined}
            disabled={!clickable}
            className={cn(
              'group flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-medium transition-all',
              isCurrent && 'bg-primary text-primary-foreground',
              isPast && 'bg-primary/10 text-primary',
              !isPast && !isCurrent && 'text-muted-foreground',
              clickable && 'cursor-pointer hover:bg-primary/15 hover:text-foreground',
              !clickable && !isCurrent && 'cursor-default',
            )}
            title={clickable ? `Jump to ${STEP_LABEL[s]}` : undefined}
          >
            <span
              className={cn(
                'grid h-4 w-4 place-items-center rounded-full text-[9px] font-semibold',
                isCurrent && 'bg-primary-foreground text-primary',
                isPast && 'bg-primary text-primary-foreground',
                !isPast && !isCurrent && 'bg-muted text-muted-foreground',
              )}
            >
              {isPast ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
            </span>
            <span className={cn(!isCurrent && 'hidden sm:inline')}>{STEP_LABEL[s]}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Step 1: Provider picker ──────────────────────────────────────

function ProviderPicker({ provider, onPick }: { provider: PartnerProvider; onPick: (p: PartnerProvider) => void }) {
  const specsQuery = useQuery({
    queryKey: ['dispatch', 'partner-specs'],
    queryFn: () => dispatchService.listProviderSpecs(),
  });
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Choose your delivery partner
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {(specsQuery.data ?? []).map((s) => (
          <button
            key={s.provider}
            type="button"
            onClick={() => onPick(s.provider)}
            disabled={s.status === 'coming-soon'}
            className={cn(
              'flex flex-col items-start gap-1.5 rounded-md border bg-card p-3 text-left transition-all',
              provider === s.provider
                ? 'border-primary ring-1 ring-primary/30 bg-primary/[0.04]'
                : 'border-border/60 hover:border-primary/40',
              s.status === 'coming-soon' && 'opacity-60 cursor-not-allowed',
            )}
          >
            <div className="flex w-full items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-primary" />
                <div className="text-[13px] font-semibold">{s.label}</div>
              </div>
              <div className="flex items-center gap-1">
                {/* M-aggregator — highlight aggregators distinctly so
                    operators don't confuse them with direct carriers. */}
                {s.kind === 'AGGREGATOR' ? (
                  <Badge variant="info" className="text-[9.5px]" title="Aggregator — one credential gives you several underlying carriers. Carrier choice + courier priority is configured inside the aggregator's own dashboard; Zafroi just hands them the order.">
                    Aggregator
                  </Badge>
                ) : s.provider !== 'MANUAL' ? (
                  <Badge variant="muted" className="text-[9.5px]" title="Direct integration with this single carrier — the rule's Service picker maps to that carrier's own service tier.">
                    Direct
                  </Badge>
                ) : null}
                {s.status === 'coming-soon' && (
                  <Badge variant="muted" className="text-[10px]">Coming soon</Badge>
                )}
                {s.status === 'available' && (
                  <Badge variant="success" className="text-[10px]">Available</Badge>
                )}
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground">{s.tagline}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 2: Identity ─────────────────────────────────────────────

function IdentityStep({
  code, name, contactName, contactPhone, contactEmail, isEdit,
  onCode, onName, onContactName, onContactPhone, onContactEmail,
}: {
  code: string; name: string; contactName: string; contactPhone: string; contactEmail: string;
  isEdit: boolean;
  onCode: (v: string) => void; onName: (v: string) => void;
  onContactName: (v: string) => void; onContactPhone: (v: string) => void; onContactEmail: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Code" htmlFor="code" required hint="Uppercase identifier — e.g. SHIPROCKET, BLUEDART, LOCAL.">
          <Input id="code" value={code} onChange={(e) => onCode(e.target.value.toUpperCase())}
                 disabled={isEdit} className="font-mono" maxLength={32} required />
        </FormField>
        <FormField label="Display name" htmlFor="name" required>
          <Input id="name" value={name} onChange={(e) => onName(e.target.value)} maxLength={200} required />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Contact name" htmlFor="contactName" hint="Optional — your account manager at the partner.">
          <Input id="contactName" value={contactName} onChange={(e) => onContactName(e.target.value)} maxLength={200} />
        </FormField>
        <FormField label="Contact phone" htmlFor="contactPhone">
          <Input id="contactPhone" value={contactPhone} onChange={(e) => onContactPhone(e.target.value)} maxLength={32} />
        </FormField>
      </div>
      <FormField label="Contact email" htmlFor="contactEmail">
        <Input id="contactEmail" type="email" value={contactEmail} onChange={(e) => onContactEmail(e.target.value)} />
      </FormField>
    </div>
  );
}

// ── Step 3: Credentials ──────────────────────────────────────────

function CredentialsStep({
  spec, credentials, onCredsChange, testResult, onTest, isTesting,
  existingPartner,
}: {
  spec: PartnerProviderSpec;
  credentials: Record<string, string>;
  onCredsChange: (c: Record<string, string>) => void;
  testResult: TestConnectionResult | null;
  onTest: () => void;
  isTesting: boolean;
  existingPartner?: DispatchPartner | null;
}) {
  // Edit mode + already-connected — show the existing connection state
  // at the top so the operator knows they don't have to re-enter
  // credentials. Empty fields below mean "no change"; typing anything
  // overrides + re-tests.
  const isEditConnected =
    !!existingPartner &&
    existingPartner.connectionStatus === 'CONNECTED' &&
    !testResult;
  const hasTypedCreds = Object.values(credentials).some((v) => v && v.length > 0);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-info/30 bg-info/[0.04] px-3 py-2 text-[12px] text-muted-foreground">
        <strong className="text-foreground">{spec.label}</strong> · {spec.tagline}
      </div>

      {isEditConnected && !hasTypedCreds && (
        <div className="flex items-start gap-2.5 rounded-md border border-success/30 bg-success/[0.05] px-3 py-2.5">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
          <div className="flex-1 text-[12.5px]">
            <div className="font-semibold text-foreground">
              Already connected
              {existingPartner?.accountInfo && (
                <span className="ml-1.5 font-normal text-muted-foreground">
                  · {existingPartner.accountInfo}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[11.5px] text-muted-foreground">
              {existingPartner?.lastTestedAt
                ? `Last verified ${new Date(existingPartner.lastTestedAt).toLocaleString()}.`
                : 'Last verified on initial setup.'}{' '}
              Leave fields below blank to keep current credentials, or type new
              values to rotate them.
            </div>
          </div>
        </div>
      )}

      {spec.setupGuide && <ProviderSetupGuidePanel spec={spec} />}
      <HowItWorksCredentials spec={spec} />
      {spec.credentialFields.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/70 px-3 py-6 text-center text-[12px] text-muted-foreground">
          No credentials required — this provider has no API to set up.
        </div>
      ) : (
        <>
          {spec.credentialFields.map((f) => (
            <FormField key={f.key} label={f.label} htmlFor={f.key} required={f.required && !isEditConnected} hint={f.hint}>
              <Input
                id={f.key}
                type={f.type === 'password' ? 'password' : f.type === 'number' ? 'number' : 'text'}
                value={credentials[f.key] ?? ''}
                onChange={(e) => onCredsChange({ ...credentials, [f.key]: e.target.value })}
                placeholder={isEditConnected ? '••• stored — leave blank to keep' : f.placeholder}
                autoComplete="off"
              />
            </FormField>
          ))}
          <div className="flex items-center gap-3 border-t border-border/60 pt-3">
            <Button onClick={onTest} disabled={isTesting || (isEditConnected && !hasTypedCreds)}>
              {isTesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {isEditConnected && !hasTypedCreds ? 'Already connected' : 'Test connection'}
            </Button>
            {testResult && (
              <div
                className={cn(
                  'flex items-center gap-1.5 text-[12px]',
                  testResult.ok ? 'text-success' : 'text-destructive',
                )}
              >
                {testResult.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                {testResult.ok ? `Connected · ${testResult.accountInfo ?? ''}` : testResult.error ?? 'Failed'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Step 4: Webhook ──────────────────────────────────────────────

function WebhookStep({
  spec, webhookUrl, secret, onRotate, isRotating, partnerId, partner,
}: {
  spec: PartnerProviderSpec;
  webhookUrl: string;
  secret: string | null;
  onRotate: () => void;
  isRotating: boolean;
  partnerId: string | null;
  partner: DispatchPartner | null;
}) {
  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };
  return (
    <div className="space-y-4">
      <FormField label="Webhook URL" htmlFor="webhookUrl" hint={`Paste this into your ${spec.label} dashboard.`}>
        <div className="flex items-center gap-2">
          <Input id="webhookUrl" value={webhookUrl} readOnly className="font-mono text-[11px]" />
          <Button variant="outline" size="sm" onClick={() => copy(webhookUrl)}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </FormField>
      <FormField label="Webhook secret" htmlFor="webhookSecret" hint={`Used to verify ${spec.webhookSpec.verificationMethod} signatures.`}>
        <div className="flex items-center gap-2">
          <Input id="webhookSecret" value={secret ?? '—'} readOnly className="font-mono text-[11px]" />
          <Button variant="outline" size="sm" onClick={() => secret && copy(secret)} disabled={!secret}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={onRotate} disabled={isRotating} title="Rotate secret">
            {isRotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </FormField>
      <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-[12px] whitespace-pre-wrap font-sans leading-relaxed">
        {spec.webhookSpec.setupInstructions}
      </div>
      {partnerId ? (
        <ConnectionHealthCard partnerId={partnerId} partner={partner} />
      ) : null}
      <HowItWorksWebhook spec={spec} />
    </div>
  );
}

/**
 * Live indicator + last 10 events for the partner currently being
 * edited. Polls every 15s while the wizard's webhook step is open so
 * the operator can watch their first inbound webhook actually land
 * after pasting the URL into the provider dashboard.
 */
function ConnectionHealthCard({
  partnerId,
  partner,
}: {
  partnerId: string;
  partner: DispatchPartner | null;
}) {
  const recentQuery = useQuery({
    queryKey: ['partner', partnerId, 'inbound-webhooks'],
    queryFn: () => partnerInboundWebhooksService.recent(partnerId, 10),
    refetchInterval: 15_000,
  });

  const lastAt = partner?.lastWebhookAt ?? null;
  const lastStatus = partner?.lastWebhookStatus ?? null;
  const lastError = partner?.lastWebhookError ?? null;

  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-card p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11.5px] font-semibold text-foreground">
          <Webhook className="h-3.5 w-3.5" />
          Connection health
        </div>
        <Link
          to="/platform/webhooks/inbound"
          className="text-[11px] text-primary hover:underline"
        >
          View full inbound log →
        </Link>
      </div>
      <div className="text-[12px] text-foreground">
        {!lastAt ? (
          <span className="text-muted-foreground">
            No webhooks received yet. Once your dashboard pushes the first event, it will appear
            here within a few seconds.
          </span>
        ) : lastStatus === 'ACCEPTED' ? (
          <span>
            <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-emerald-500" />
            Last received <span className="font-mono">{fmt.dateTime(lastAt)}</span>{' '}
            <span className="text-muted-foreground">· accepted</span>
          </span>
        ) : (
          <span className="text-destructive">
            <XCircle className="mr-1 inline h-3.5 w-3.5" />
            Last attempt {fmt.dateTime(lastAt)} · rejected{lastError ? ` — ${lastError}` : ''}
          </span>
        )}
      </div>
      {recentQuery.data && recentQuery.data.length > 0 ? (
        <div className="rounded border border-border/50 bg-muted/10">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-2 py-1 text-left font-semibold">Received</th>
                <th className="px-2 py-1 text-left font-semibold">Status</th>
                <th className="px-2 py-1 text-left font-semibold">AWB</th>
                <th className="px-2 py-1 text-left font-semibold">Note</th>
              </tr>
            </thead>
            <tbody>
              {recentQuery.data.map((e) => (
                <tr key={e.id} className="border-b border-border/30 last:border-0 text-[11.5px]">
                  <td className="whitespace-nowrap px-2 py-1 font-mono tabular-nums text-muted-foreground">
                    {fmt.dateTime(e.receivedAt)}
                  </td>
                  <td className="px-2 py-1">
                    {e.status === 'ACCEPTED' ? (
                      <Badge variant="success" className="h-4 text-[9.5px]">
                        accepted
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="h-4 text-[9.5px]">
                        rejected
                      </Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 font-mono text-[11px]">
                    {e.awbNumber ?? '—'}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground">
                    {e.errorReason ?? e.rawStatus ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

// ── "How it works" panels — one per step that benefits from
//    explaining the under-the-hood flow to the operator.

/**
 * Per-provider guided onboarding rendered above the credential fields.
 * Powered by `spec.setupGuide` — adapters opt in by populating that
 * field with a list of numbered steps (DHL has one; Shiprocket's setup
 * is simple enough that `HowItWorksCredentials` covers it). Default
 * expanded so first-time setup is walked through inline; operator can
 * collapse if they already know the drill.
 */
function ProviderSetupGuidePanel({ spec }: { spec: PartnerProviderSpec }) {
  const [open, setOpen] = useState(true);
  if (!spec.setupGuide) return null;
  const guide = spec.setupGuide;
  return (
    <div className="rounded-md border border-warning/30 bg-warning/[0.04]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
      >
        <span className="flex items-center gap-2 text-[12.5px] font-semibold text-foreground">
          <HelpCircle className="h-3.5 w-3.5 text-[hsl(38_85%_45%)]" />
          How to get your {spec.label} credentials
        </span>
        <span className="text-[11px] text-muted-foreground">
          {open ? 'Collapse' : 'Show steps'}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-warning/20 px-3 py-3">
          {guide.intro && (
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              {guide.intro}
            </p>
          )}
          <ol className="space-y-3">
            {guide.steps.map((step, idx) => (
              <li key={idx} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[hsl(38_85%_45%)] text-[10.5px] font-semibold text-white"
                >
                  {idx + 1}
                </span>
                <div className="flex-1 space-y-1">
                  <div className="text-[12.5px] font-semibold text-foreground">
                    {step.title}
                  </div>
                  <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                    {step.body}
                  </p>
                  {step.link && (
                    <a
                      href={step.link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11.5px] font-medium text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {step.link.label}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function HowItWorksCredentials({ spec }: { spec: PartnerProviderSpec }) {
  // Provider-aware copy. Shiprocket has the most non-obvious flow
  // (login creds → JWT exchange) so we explain it in detail. Direct
  // carriers get a shorter "API key in / Bearer out" version.
  const isShiprocket = spec.provider === 'SHIPROCKET';
  return (
    <div className="space-y-2 rounded-md border border-primary/20 bg-primary/[0.03] p-3">
      <div className="flex items-center gap-2 text-[11.5px] font-semibold text-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
        How this works
      </div>
      {isShiprocket ? (
        <ul className="space-y-1.5 pl-5 text-[11.5px] text-muted-foreground" style={{ listStyleType: 'disc' }}>
          <li>
            Shiprocket doesn't issue separate API keys — the credentials you
            use to <em>log into the Shiprocket dashboard</em> ARE the API
            credentials.
          </li>
          <li>
            We POST them once to{' '}
            <span className="font-mono">/v1/external/auth/login</span> and
            Shiprocket returns a JWT token valid ~10 days. Every shipment we
            create uses that token (cached server-side; refreshed before it
            expires).
          </li>
          <li>
            Your password is encrypted with <strong>AES-256-GCM</strong>{' '}
            before it touches Postgres. Decrypted only in-memory at call
            time. Never logged, never returned in API responses.
          </li>
          <li>
            <strong className="text-foreground">Best practice:</strong> create
            a dedicated API user (Shiprocket dashboard →{' '}
            <em>Settings → API → Manage Users → Add User</em>) and enter
            those credentials here instead of your personal owner login.
            Revoke it independently if anything goes wrong.
          </li>
        </ul>
      ) : (
        <ul className="space-y-1.5 pl-5 text-[11.5px] text-muted-foreground" style={{ listStyleType: 'disc' }}>
          <li>
            Credentials are encrypted with <strong>AES-256-GCM</strong>{' '}
            before they touch our database. Decrypted only in-memory at
            call time.
          </li>
          <li>
            On each shipment we authenticate to {spec.label} and pass the
            order. Never logged, never returned in API responses.
          </li>
          <li>
            <strong className="text-foreground">Best practice:</strong> if
            your carrier supports a dedicated API account separate from
            your dashboard owner, use that here.
          </li>
        </ul>
      )}
    </div>
  );
}

function HowItWorksWebhook({ spec }: { spec: PartnerProviderSpec }) {
  return (
    <div className="space-y-2 rounded-md border border-primary/20 bg-primary/[0.03] p-3">
      <div className="flex items-center gap-2 text-[11.5px] font-semibold text-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
        How this works
      </div>
      <ul className="space-y-1.5 pl-5 text-[11.5px] text-muted-foreground" style={{ listStyleType: 'disc' }}>
        <li>
          {spec.label} POSTs tracking updates (out-for-delivery, delivered, …)
          to the URL above whenever a shipment's status changes.
        </li>
        <li>
          The webhook secret is verified on every call using{' '}
          <span className="font-mono">{spec.webhookSpec.verificationMethod}</span>
          {' '}so an attacker can't forge fake tracking events. Rotate the
          secret here if you ever leak the URL.
        </li>
        <li>
          We map the partner's status onto our own enum
          (<span className="font-mono">IN_TRANSIT</span>,{' '}
          <span className="font-mono">DELIVERED</span>, …), write a
          tracking-history row, and fire your own outbound webhooks
          (<span className="font-mono">dispatch.status.changed</span>,{' '}
          <span className="font-mono">tracking.event.recorded</span>) so
          your portal can react.
        </li>
      </ul>
    </div>
  );
}

// ── Step 5: Pickup + services ────────────────────────────────────

function PickupServicesStep({
  spec, warehouses, pickupWarehouseId, onPickupChange, enabledServices, onServicesChange,
}: {
  spec: PartnerProviderSpec;
  warehouses: Array<{ id: string; code: string; name: string }>;
  pickupWarehouseId: string;
  onPickupChange: (id: string) => void;
  enabledServices: PartnerService[];
  onServicesChange: (s: PartnerService[]) => void;
}) {
  const toggleService = (s: PartnerService) => {
    const has = enabledServices.some((x) => x.code === s.code);
    if (has) onServicesChange(enabledServices.filter((x) => x.code !== s.code));
    else onServicesChange([...enabledServices, s]);
  };
  return (
    <div className="space-y-4">
      <FormField label="Pickup warehouse" htmlFor="pickup" hint="The warehouse this partner picks up from. Register-with-partner happens after first dispatch.">
        <Select value={pickupWarehouseId} onValueChange={onPickupChange}>
          <SelectTrigger id="pickup"><SelectValue placeholder="Pick warehouse" /></SelectTrigger>
          <SelectContent>
            {warehouses.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                <span className="font-mono text-[12px]">{w.code}</span>
                <span className="ml-2 text-[11px] text-muted-foreground">{w.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="Services" htmlFor="services" hint={`Which ${spec.label} services should the routing engine consider?`}>
        <div className="grid grid-cols-2 gap-2">
          {spec.serviceCatalog.map((s) => {
            const checked = enabledServices.some((x) => x.code === s.code);
            return (
              <button
                key={s.code}
                type="button"
                onClick={() => toggleService(s)}
                className={cn(
                  'flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-left text-[12px] transition-all',
                  checked
                    ? 'border-primary ring-1 ring-primary/30 bg-primary/[0.04]'
                    : 'border-border/60 hover:border-primary/40',
                )}
              >
                <div className={cn('h-3.5 w-3.5 rounded border', checked ? 'border-primary bg-primary' : 'border-border')}>
                  {checked && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-foreground">{s.label}</div>
                  {s.isCod && (
                    <Badge variant="muted" className="mt-0.5 text-[9.5px]">
                      <CreditCard className="mr-1 h-2.5 w-2.5" />COD
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </FormField>
    </div>
  );
}

// ── Step 6: Review ───────────────────────────────────────────────

/**
 * Phase 2 — Portal exposure step.
 *
 * Lets the operator decide whether this partner shows up as a paid
 * shipping option on the customer-facing portal. When toggled on,
 * the operator picks the customer-facing label, a flat shipping
 * charge, and an optional ETA hint. When off (default), only the
 * Standard option (free, system routes via assignment rules) is
 * visible on the portal — current behaviour preserved.
 */
function PortalStep({
  providerLabel,
  fallbackName,
  exposedToPortal,
  onExposedChange,
  portalDisplayName,
  onDisplayNameChange,
  portalBaseRate,
  onBaseRateChange,
  portalRateUsd,
  onUsdRateChange,
  portalEtaDays,
  onEtaDaysChange,
}: {
  providerLabel: string;
  fallbackName: string;
  exposedToPortal: boolean;
  onExposedChange: (v: boolean) => void;
  portalDisplayName: string;
  onDisplayNameChange: (v: string) => void;
  portalBaseRate: string;
  onBaseRateChange: (v: string) => void;
  portalRateUsd: string;
  onUsdRateChange: (v: string) => void;
  portalEtaDays: string;
  onEtaDaysChange: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-info/30 bg-info/[0.04] px-3 py-2 text-[12px] text-muted-foreground">
        Phase 2 — let customers pick this partner directly on the portal.
        When off, the partner is operator-only and routes via your assignment
        rules (the implicit "Standard" option). When on, customers see this
        partner as a paid shipping choice alongside Standard.
      </div>

      <label className="flex items-start gap-3 rounded-md border border-border/60 bg-card px-4 py-3 cursor-pointer">
        <input
          type="checkbox"
          checked={exposedToPortal}
          onChange={(e) => onExposedChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
        />
        <div className="flex-1">
          <div className="text-[13px] font-semibold">Expose to customer portal</div>
          <div className="text-[11.5px] text-muted-foreground">
            {exposedToPortal
              ? `Customers will see this partner ("${portalDisplayName.trim() || fallbackName || providerLabel}") as a paid option on /utilities/shipping-options.`
              : 'Operator-only — customers never see this partner on the portal. Standard routing applies.'}
          </div>
        </div>
      </label>

      {exposedToPortal && (
        <div className="space-y-3 rounded-md border border-border/60 bg-muted/10 px-4 py-3">
          <FormField
            label="Customer-facing display name"
            htmlFor="portalDisplayName"
            hint={`Falls back to "${fallbackName || providerLabel}" when empty.`}
          >
            <Input
              id="portalDisplayName"
              value={portalDisplayName}
              onChange={(e) => onDisplayNameChange(e.target.value)}
              placeholder="DHL Express International"
              maxLength={120}
            />
          </FormField>
          <div className="grid grid-cols-3 gap-3">
            <FormField
              label="Charge — INR (₹)"
              htmlFor="portalBaseRate"
              hint="Flat shipping charge for India (INR) orders."
              required
            >
              <Input
                id="portalBaseRate"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={portalBaseRate}
                onChange={(e) => onBaseRateChange(e.target.value)}
                placeholder="500.00"
                className="text-right font-mono"
              />
            </FormField>
            <FormField
              label="Charge — USD ($)"
              htmlFor="portalRateUsd"
              hint="Flat shipping charge for international (USD) orders."
            >
              <Input
                id="portalRateUsd"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={portalRateUsd}
                onChange={(e) => onUsdRateChange(e.target.value)}
                placeholder="9.00"
                className="text-right font-mono"
              />
            </FormField>
            <FormField
              label="ETA hint (days)"
              htmlFor="portalEtaDays"
              hint='Surfaced as "Arrives in N days". Optional.'
            >
              <Input
                id="portalEtaDays"
                type="number"
                inputMode="numeric"
                min="1"
                max="365"
                value={portalEtaDays}
                onChange={(e) => onEtaDaysChange(e.target.value)}
                placeholder="5"
                className="text-right font-mono"
              />
            </FormField>
          </div>
          <div className="rounded-md bg-warning/[0.05] border border-warning/20 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
            The flat rate is operator-set for now. Live rate-shop against the
            carrier's API is a future slice (D-15) — when that lands, this
            field becomes a fallback for when the rate API is unreachable.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Phase 1 — destructive partner actions surfaced on the Review step
 * when editing an existing partner. Two actions:
 *   • Inactivate / Activate — toggles partner.status between
 *     ACTIVE and INACTIVE. Routing rules pointing at an INACTIVE
 *     partner stop matching; operator can flip back any time.
 *   • Delete — BE hard-deletes when no dispatch/rule history exists,
 *     otherwise soft-deletes (status=ARCHIVED) so historical dispatch
 *     rows still resolve the carrier name. Toast phrasing reflects
 *     which path the BE took.
 *
 * Native window.confirm for the delete prompt — small surface, matches
 * the rest of the app's destructive flows; a custom alert-dialog is a
 * follow-up if this slice's UX needs upgrading.
 */
function PartnerDangerZone({
  status,
  isToggling,
  onToggleStatus,
  isDeleting,
  onDelete,
}: {
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  isToggling: boolean;
  onToggleStatus: () => void;
  isDeleting: boolean;
  onDelete: () => void;
}) {
  const isActive = status === 'ACTIVE';
  const isArchived = status === 'ARCHIVED';
  return (
    <div className="mt-4 space-y-3 rounded-md border border-destructive/30 bg-destructive/[0.03] p-3">
      <div className="flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" />
        Danger zone
      </div>
      {isArchived ? (
        <div className="text-[12px] text-muted-foreground">
          This partner has been archived. Past dispatches still reference it
          for tracking + audit, but new dispatches won't route here. Re-create
          a fresh partner row if you need to resume booking via this carrier.
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-[12.5px] font-medium">
                {isActive ? 'Inactivate partner' : 'Re-activate partner'}
              </div>
              <div className="text-[11.5px] text-muted-foreground">
                {isActive
                  ? 'Routing rules pointing at this partner stop matching. Operator can flip back any time. No dispatch history is touched.'
                  : 'Partner re-joins the routing pool. Existing rules pointing here start matching again.'}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleStatus}
              disabled={isToggling}
            >
              {isToggling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : isActive ? (
                <PowerOff className="h-3.5 w-3.5" />
              ) : (
                <Power className="h-3.5 w-3.5" />
              )}
              {isActive ? 'Inactivate' : 'Activate'}
            </Button>
          </div>
          <div className="flex items-start justify-between gap-3 border-t border-destructive/15 pt-3">
            <div className="flex-1">
              <div className="text-[12.5px] font-medium">Delete partner</div>
              <div className="text-[11.5px] text-muted-foreground">
                Removes the partner from pickers + rule engine. If past
                dispatches reference this partner, the row is archived
                (soft-deleted) so tracking + audit still resolve it.
                Otherwise it's hard-deleted.
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              disabled={isDeleting}
              onClick={() => {
                if (window.confirm('Delete this partner? Past dispatches will keep history; new routing rules can no longer pick it.')) {
                  onDelete();
                }
              }}
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              Delete
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ReviewStep({
  provider, spec, code, name, testResult, hasWebhookSecret,
  pickupWarehouseId, enabledServices, existingPartner,
}: {
  provider: PartnerProvider;
  spec: PartnerProviderSpec | undefined;
  code: string;
  name: string;
  testResult: TestConnectionResult | null;
  hasWebhookSecret: boolean;
  pickupWarehouseId: string;
  enabledServices: PartnerService[];
  existingPartner?: DispatchPartner | null;
}) {
  // "Connected" check honors the persisted partner connection in edit
  // mode — testResult is null on a fresh edit-mode mount (we don't
  // synthesize one from existingPartner) but the partner row may
  // already be CONNECTED with accountInfo stamped. Earlier code only
  // looked at testResult, which made the Review checklist falsely show
  // ❌ "Connected to DHL Express" for partners that were perfectly
  // connected.
  const isConnected =
    testResult?.ok === true ||
    existingPartner?.connectionStatus === 'CONNECTED';
  const accountInfo =
    testResult?.accountInfo ?? existingPartner?.accountInfo ?? null;

  const checks: Array<[boolean, string]> = provider === 'MANUAL'
    ? [[!!code, 'Code set'], [!!name, 'Name set']]
    : [
      [!!code, 'Code set'],
      [!!name, 'Name set'],
      [isConnected, `Connected to ${spec?.label}${accountInfo ? ` (${accountInfo})` : ''}`],
      [hasWebhookSecret, 'Webhook URL + secret ready to copy'],
      [!!pickupWarehouseId, 'Pickup warehouse selected'],
      [enabledServices.length > 0, `${enabledServices.length} service${enabledServices.length === 1 ? '' : 's'} enabled`],
    ];
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-info/30 bg-info/[0.04] px-3 py-2 text-[12.5px]">
        <strong>{name}</strong> · provider <span className="font-mono">{provider}</span>
      </div>
      {checks.map(([ok, label], i) => (
        <div key={i} className="flex items-center gap-2 text-[12.5px]">
          {ok
            ? <CheckCircle2 className="h-3.5 w-3.5 text-success" />
            : <XCircle className="h-3.5 w-3.5 text-destructive" />
          }
          <span className={ok ? 'text-foreground' : 'text-destructive'}>{label}</span>
        </div>
      ))}
    </div>
  );
}
